import React, { useState, useEffect, useRef } from "react";
import { Pin, Users, CheckCheck, MailOpen, MoreVertical } from "lucide-react";
import { getRelativeTime } from "../../data/mockChatData.jsx";
import { getStatusColor } from "../../services/chatService";

/**
 * 💬 CONVERSATION ITEM COMPONENT
 * Memoized component untuk performance optimization
 */
const ConversationItem = React.memo(
  ({
    conversation,
    isSelected,
    mediaIcon,
    channelIcon,
    onSelect,
    onMarkAsRead,
  }) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    const handleClick = () => {
      onSelect(conversation.id);
    };

    // Click outside handler for mobile menu
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setShowMenu(false);
        }
      };

      if (showMenu) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [showMenu]);

    const handleMarkAsReadClick = (e) => {
      e.stopPropagation();
      onMarkAsRead(conversation.id);
      setShowMenu(false);
    };

    const handleThreeDotsClick = (e) => {
      e.stopPropagation();
      setShowMenu(!showMenu);
    };

    return (
      <div
        onClick={handleClick}
        className={`
        flex items-start gap-3 p-3 cursor-pointer border-b border-gray-100 dark:border-gray-700
        hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors active:bg-gray-200 dark:active:bg-gray-600
        ${
          isSelected
            ? "bg-green-100 dark:bg-green-900/20 border-r-4 border-r-green-500"
            : ""
        }
      `}
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
            <span className="text-gray-600 dark:text-gray-300 font-semibold text-sm">
              {(conversation.customer_name || "Unknown")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)}
            </span>
          </div>
          {/* Online status */}
          {conversation.is_online && (
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
          )}
          {/* Pinned indicator */}
          {conversation.is_pinned && (
            <Pin className="absolute -top-1 -right-1 w-3 h-3 text-gray-500 fill-gray-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate flex-1 min-w-0">
                {conversation.customer_name || "Unknown Customer"}
              </h3>
              {/* <span className="text-xs flex-shrink-0">{channelIcon}</span> */}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {getRelativeTime(conversation.last_message_at)}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0 relative">
              {/* Three Dots Button - All Devices */}
              {conversation.unread_count > 0 && (
                <button
                  onClick={handleThreeDotsClick}
                  className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 
                             text-gray-400 active:bg-gray-300 dark:active:bg-gray-600
                             transition-colors"
                  title="Options"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              )}

              {/* Dropdown Menu - All Devices */}
              {showMenu && (
                <div
                  ref={menuRef}
                  className="absolute right-0 top-full mt-1 z-50 
                             bg-white dark:bg-gray-800 
                             border border-gray-200 dark:border-gray-700 
                             rounded-lg shadow-lg py-2 min-w-[160px]"
                >
                  {conversation.unread_count > 0 && (
                    <button
                      onClick={handleMarkAsReadClick}
                      className="w-full px-4 py-2.5 text-left text-sm 
                                 hover:bg-gray-100 dark:hover:bg-gray-700 
                                 flex items-center gap-2
                                 text-gray-700 dark:text-gray-300"
                    >
                      <MailOpen className="w-4 h-4" />
                      Mark as Read
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Last message */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 flex-1 min-w-0">
              {mediaIcon && (
                <span className="text-xs flex-shrink-0">{mediaIcon}</span>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate flex-1 min-w-0">
                {conversation.last_message}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
              {/* Message status for sent messages */}
              {conversation.status === "active" && (
                <div className="flex text-gray-400">
                  <CheckCheck className="w-3 h-3" />
                </div>
              )}
              {/* Unread count */}
              {conversation.unread_count > 0 && (
                <div className="bg-green-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {conversation.unread_count}
                </div>
              )}
            </div>
          </div>

          {/* Status and assignment - Two row layout */}
          <div className="mt-2 space-y-1">
            {/* Row 1: Status and Assignment */}
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(
                  conversation.status
                )}`}
              >
                {conversation.status}
              </span>
              {conversation.assigned_to && (
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate min-w-0 flex-1">
                  <Users className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{conversation.assigned_to}</span>
                </span>
              )}
            </div>

            {/* Row 2: Escalation Reason - Only show for escalated or assigned status */}
            {conversation.escalation_reason &&
              (conversation.status === "escalated" ||
                conversation.status === "assigned") && (
                <div className="flex items-center min-w-0">
                  <span className="text-xs text-orange-600 dark:text-orange-400 truncate flex-1 min-w-0">
                    {conversation.escalation_reason}
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  }
);

ConversationItem.displayName = "ConversationItem";

export default ConversationItem;
